import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState, useCallback } from "react";
import classNames from "classnames";
import { Nav, NavItem, NavLink, TabContent, TabPane, Row, Col, FormGroup, Label, Input, FormText, Button } from "reactstrap";
import ReactDatetime from "components/CustomDatePicker";
import { useTranslation } from "react-i18next";
import { useUpdateDeal } from "api/deals/service";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import { formatPriceWithDollar } from "utils/currency";
import { formatDateForAPI, formatDateForPicker } from "utils/dateUtils";
import { formatPhoneNumber } from "utils/phoneUtils";
import DealApplicantReferences from "./DealApplicantReferences";
import IdentityInformationForm from "components/Customers/IdentityInformationForm";
import { useCreateCustomer } from "api/customers/service";
import { useGetCountries } from "api/countries/service";
import Select from "components/Select";
import StatesInput from "components/StatesInput";

const DealApplicantTab = forwardRef(({ data, isLoading, id, activeColor = "blue", tab, isTheme2 = false }, ref) => {
  const { t } = useTranslation();
  const updateDealMutation = useUpdateDeal();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".deal-applicant-tab.deal-applicant-tab--theme2",
            fieldsSelector: ".deal-applicant-identity-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle14 = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0, fontSize: "14px" };
  const dateInputStyle = isTheme2
    ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR }
    : { borderRadius: 0 };
  const [openAddArea, setOpenAddArea] = useState(false);
  const [activeSubTab, setActiveSubTab] = useState(() => {
    // localStorage'dan activeSubTab'ı al, yoksa "INFO" kullan
    return localStorage.getItem('dealApplicantActiveSubTab') || "INFO";
  });
  const { data: countriesData } = useGetCountries();

  const handleClose = useCallback(() => {
    setOpenAddArea(false);
  }, []);

  const handleSubTabChange = useCallback((newTab) => {
    setActiveSubTab(newTab);
    localStorage.setItem('dealApplicantActiveSubTab', newTab);
  }, []);

  const createCustomerMutation = useCreateCustomer({ onClose: handleClose });
  const [identityData, setIdentityData] = useState({
    full_name: "",
    ssn: "",
    relationship_type: "Unknown"
  });

  const [formData, setFormData] = useState({
    full_name: "",
    ssn: "",
    date_of_birth: "",
    gender: "",
    active_service_member: false,
    dl: "",
    dl_date: "",
    dl_state: null,
    dl_expiration: "",
    marital_status: "",
    is_business: false,
    doing_business: "",
    fed_id: "",
    email: "",
    cell_phone: "",
    home_phone: "",
    home_phone_2: "",
    work_phone: "",
    work_extension: "",
    work_phone_2: "",
    work_extension_2: "",
    alt_id_type: "",
    alt_id_number: "",
    alt_id_country: "",
    alt_id_state: null,
    // Current Address
    current_address: {
      country: "",
      address_1: "",
      address_2: "",
      zip_code: "",
      city: "",
      county: "",
      state: "",
      years_at: "",
      months_at: "",
      lien_holder: "",
      lien_holder_phone: "",
      residence_payment: "",
      residence_type: "",
      miles_from_lot: "",
    },
    previous_address: {
      prev_country: "",
      prev_address_1: "",
      prev_address_2: "",
      prev_zip_code: "",
      prev_city: "",
      prev_county: "",
      prev_state: "",
      prev_years_at: "",
      prev_months_at: "",
      prev_lien_holder: "",
      prev_lien_holder_phone: "",
      prev_residence_payment: "",
      prev_residence_type: "",
      prev_miles_from_lot: "",
    },
    // Current Employment
    current_employment: {
      employer: "",
      supervisor_phone: "",
      position: "",
      type: "",
      income: "",
      pay_frequency: "",
      years_at: "",
      months_at: "",
      address_1: "",
      address_2: "",
      zip_code: "",
      city: "",
      county: "",
      state: "",
      country: "",
      email: "",
    },
    // Previous Employment
    previous_employment: {
      employer: "",
      supervisor_phone: "",
      position: "",
      type: "",
      income: "",
      pay_frequency: "",
      years_at: "",
      months_at: "",
      address_1: "",
      address_2: "",
      zip_code: "",
      city: "",
      county: "",
      state: "",
      country: "",
      email: "",
    },
    // Other Monthly Income (3 rows)
    other_monthly_income: {
      other_income_1_type: "",
      other_income_1_desc: "",
      other_income_1_amt: "",
      other_income_2_type: "",
      other_income_2_desc: "",
      other_income_2_amt: "",
      other_income_3_type: "",
      other_income_3_desc: "",
      other_income_3_amt: "",
    },
    assets: {
      asset_1_type: "",
      asset_1_desc: "",
      asset_1_value: "",
      asset_2_type: "",
      asset_2_desc: "",
      asset_2_value: "",
      asset_3_type: "",
      asset_3_desc: "",
      asset_3_value: "",
    },
    debts: {
      debt_1_creditor: "",
      debt_1_type: "",
      debt_1_payment: "",
      debt_1_balance: "",
      debt_2_creditor: "",
      debt_2_type: "",
      debt_2_payment: "",
      debt_2_balance: "",
      debt_3_creditor: "",
      debt_3_type: "",
      debt_3_payment: "",
      debt_3_balance: "",
    },
    dependents: {
      dependents_count: "",
      dependents_1_age: "",
      dependents_2_age: "",
      dependents_3_age: "",
      dependents_4_age: "",
      dependents_5_age: "",
      dependents_6_age: "",
      dependents_7_age: "",
    },
    // Prior Repossession
    prior_repossession: false,
    repossession_year: "",
    repossession_reason: "",
    // Bankruptcy
    prior_bankruptcy: false,
    bankruptcy_type: "",
    bankruptcy_discharge: "",
    bankruptcy_reason: "",
    currently_in_bankruptcy: false,
    future_bankruptcy_anticipated: false,
    // Judgment
    prior_judgment: false,
    judgment_amount: "",
    judgment_to_whom: "",
    // Guarantor
    currently_guarantor: false,
    guarantor_for_whom: "",
    guarantor_to: "",
  });

  const findCountry = (value) => {
    if (!countriesData?.data) return undefined;
    return countriesData.data.find((c) => String(c.id) === String(value) || c.name === value || c.documentId === value);
  };
  const selectedAltIdCountry = useMemo(() => findCountry(formData.alt_id_country), [countriesData, formData.alt_id_country]);
  const altIdStateOptions = selectedAltIdCountry?.states || [];
  const altIdHasStates = Array.isArray(altIdStateOptions) && altIdStateOptions.length > 0;

  const selectedCurrAddrCountry = useMemo(() => findCountry(formData.current_address?.country), [countriesData, formData.current_address?.country]);
  const currAddrStateOptions = selectedCurrAddrCountry?.states || [];
  const currAddrHasStates = Array.isArray(currAddrStateOptions) && currAddrStateOptions.length > 0;

  const selectedPrevAddrCountry = useMemo(() => findCountry(formData.previous_address?.prev_country), [countriesData, formData.previous_address?.prev_country]);
  const prevAddrStateOptions = selectedPrevAddrCountry?.states || [];
  const prevAddrHasStates = Array.isArray(prevAddrStateOptions) && prevAddrStateOptions.length > 0;

  const selectedEmploymentCountry = useMemo(() => findCountry(formData.current_employment?.country), [countriesData, formData.current_employment?.country]);
  const employmentStateOptions = selectedEmploymentCountry?.states || [];
  const employmentHasStates = Array.isArray(employmentStateOptions) && employmentStateOptions.length > 0;

  const selectedPrevEmploymentCountry = useMemo(() => findCountry(formData.previous_employment?.country), [countriesData, formData.previous_employment?.country]);
  const prevEmploymentStateOptions = selectedPrevEmploymentCountry?.states || [];
  const prevEmploymentHasStates = Array.isArray(prevEmploymentStateOptions) && prevEmploymentStateOptions.length > 0;


  useEffect(() => {
    if (!data) return;
    setFormData(prev => ({
      full_name: data.customer?.full_name || "",
      ssn: data.customer?.ssn || "",
      date_of_birth: data.customer?.date_of_birth || "",
      gender: data.customer?.gender || "",
      dl: data.customer?.dl || "",
      dl_date: data.customer?.dl_date || "",
      dl_state: data.customer?.dl_state,
      dl_exp: data.customer?.dl_exp || "",
      marital_status: data.marital_status || "",
      is_business: Boolean(data.customer?.is_business) || false,
      doing_business: data.customer?.doing_business || "",
      fed_id: data.customer?.fed_id || "",
      email: data.customer?.email || "",
      cell_phone: data.customer?.cell_phone || "",
      home_phone: data.customer?.home_phone || "",
      home_phone_2: data.customer?.home_phone_2 || "",
      work_phone: data.customer?.work_phone || "",
      work_extension: data.customer?.work_extension || "",
      work_phone_2: data.customer?.work_phone_2 || "",
      work_extension_2: data.customer?.work_extension_2 || "",
      alt_id_type: data.customer?.alt_id_type || "",
      alt_id_number: data.customer?.alt_id_number || "",
      alt_id_country: data.customer?.alt_id_country || "",
      alt_id_state: data.customer?.alt_id_state || null,
      current_address: {
        country: data?.customer?.current_address?.country,
        address_1: data?.customer?.current_address?.address_1,
        address_2: data?.customer?.current_address?.address_2,
        zip_code: data?.customer?.current_address?.zip_code,
        city: data?.customer?.current_address?.city,
        county: data?.customer?.current_address?.county,
        state: data?.customer?.current_address?.state,
        state_name: data?.customer?.current_address?.state_name,
        years_at: data?.customer?.current_address?.years_at,
        months_at: data?.customer?.current_address?.months_at,
        lien_holder: data?.customer?.current_address?.lien_holder,
        lien_holder_phone: data?.customer?.current_address?.lien_holder_phone,
        residence_payment: formatPriceWithDollar(data?.customer?.current_address?.residence_payment),
        residence_type: data?.customer?.current_address?.residence_type,
        miles_from_lot: data?.customer?.current_address?.miles_from_lot,
      },
      // Previous Address
      previous_address: {
        prev_country: data?.customer?.previous_address?.prev_country,
        prev_address_1: data?.customer?.previous_address?.prev_address_1 || "",
        prev_address_2: data?.customer?.previous_address?.prev_address_2 || "",
        prev_zip_code: data?.customer?.previous_address?.prev_zip_code || "",
        prev_city: data?.customer?.previous_address?.prev_city || "",
        prev_county: data?.customer?.previous_address?.prev_county || "",
        prev_state: data?.customer?.previous_address?.prev_state,
        prev_years_at: data?.customer?.previous_address?.years_at ? String(data?.customer?.previous_address?.years_at) : "",
        prev_months_at: data?.customer?.previous_address?.months_at ? String(data?.customer?.previous_address?.months_at) : "",
        prev_lien_holder: data?.customer?.previous_address?.prev_lien_holder || "",
        prev_lien_holder_phone: data?.customer?.previous_address?.prev_lien_holder_phone || "",
        prev_residence_payment: data?.customer?.previous_address?.prev_residence_payment ? formatPriceWithDollar(data?.customer?.previous_address?.prev_residence_payment) : "",
        prev_residence_type: data?.customer?.previous_address?.prev_residence_type || "",
        prev_miles_from_lot: data?.customer?.previous_address?.prev_miles_from_lot ? String(data?.customer?.previous_address?.prev_miles_from_lot) : "",
      },
      // Current Employment
      current_employment: {
        employer: data?.customer?.current_employment?.employer || "",
        supervisor_phone: data?.customer?.current_employment?.supervisor_phone || "",
        position: data?.customer?.current_employment?.position || "",
        type: data?.customer?.current_employment?.type || "",
        income: data?.customer?.current_employment?.income !== null && data?.customer?.current_employment?.income !== undefined ? formatPriceWithDollar(data?.customer?.current_employment?.income) : "",
        pay_frequency: data?.customer?.current_employment?.pay_frequency || "",
        years_at: data?.customer?.current_employment?.years_at ? String(data?.customer?.current_employment?.years_at) : "",
        months_at: data?.customer?.current_employment?.months_at ? String(data?.customer?.current_employment?.months_at) : "",
        address_1: data?.customer?.current_employment?.address_1 || "",
        address_2: data?.customer?.current_employment?.address_2 || "",
        zip_code: data?.customer?.current_employment?.zip_code || "",
        city: data?.customer?.current_employment?.city || "",
        county: data?.customer?.current_employment?.county || "",
        state: data?.customer?.current_employment?.state ? String(data?.customer?.current_employment?.state) : "",
        country: data?.customer?.current_employment?.country || "",
        email: data?.customer?.current_employment?.email || "",
      },
      // Previous Employment
      previous_employment: {
        employer: data?.customer?.previous_employment?.employer || "",
        supervisor_phone: data?.customer?.previous_employment?.supervisor_phone || "",
        position: data?.customer?.previous_employment?.position || "",
        type: data?.customer?.previous_employment?.type || "",
        income: data?.customer?.previous_employment?.income !== null && data?.customer?.previous_employment?.income !== undefined ? formatPriceWithDollar(data?.customer?.previous_employment?.income) : "",
        pay_frequency: data?.customer?.previous_employment?.pay_frequency || "",
        years_at: data?.customer?.previous_employment?.years_at ? String(data?.customer?.previous_employment?.years_at) : "",
        months_at: data?.customer?.previous_employment?.months_at ? String(data?.customer?.previous_employment?.months_at) : "",
        address_1: data?.customer?.previous_employment?.address_1 || "",
        address_2: data?.customer?.previous_employment?.address_2 || "",
        zip_code: data?.customer?.previous_employment?.zip_code || "",
        city: data?.customer?.previous_employment?.city || "",
        county: data?.customer?.previous_employment?.county || "",
        state: data?.customer?.previous_employment?.state ? String(data?.customer?.previous_employment?.state) : "",
        country: data?.customer?.previous_employment?.country || "",
        email: data?.customer?.previous_employment?.email || "",
      },
      // Other Monthly Income
      other_monthly_income: {
        other_income_1_type: data?.deals_applicant?.other_monthly_income?.other_income_1_type || "",
        other_income_1_desc: data?.deals_applicant?.other_monthly_income?.other_income_1_desc || "",
        other_income_1_amt: data?.deals_applicant?.other_monthly_income?.other_income_1_amt ? formatPriceWithDollar(data?.deals_applicant?.other_monthly_income?.other_income_1_amt) : "",
        other_income_2_type: data?.deals_applicant?.other_monthly_income?.other_income_2_type || "",
        other_income_2_desc: data?.deals_applicant?.other_monthly_income?.other_income_2_desc || "",
        other_income_2_amt: data?.deals_applicant?.other_monthly_income?.other_income_2_amt ? formatPriceWithDollar(data?.deals_applicant?.other_monthly_income?.other_income_2_amt) : "",
        other_income_3_type: data?.deals_applicant?.other_monthly_income?.other_income_3_type || "",
        other_income_3_desc: data?.deals_applicant?.other_monthly_income?.other_income_3_desc || "",
        other_income_3_amt: data?.deals_applicant?.other_monthly_income?.other_income_3_amt ? formatPriceWithDollar(data?.deals_applicant?.other_monthly_income?.other_income_3_amt) : "",
      },
      assets: {
        asset_1_type: data?.deals_applicant?.assets?.asset_1_type || "",
        asset_1_desc: data?.deals_applicant?.assets?.asset_1_desc || "",
        asset_1_value: data?.deals_applicant?.assets?.asset_1_value ? formatPriceWithDollar(data?.deals_applicant?.assets?.asset_1_value) : "",
        asset_2_type: data?.deals_applicant?.assets?.asset_2_type || "",
        asset_2_desc: data?.deals_applicant?.assets?.asset_2_desc || "",
        asset_2_value: data?.deals_applicant?.assets?.asset_2_value ? formatPriceWithDollar(data?.deals_applicant?.assets?.asset_2_value) : "",
        asset_3_type: data?.deals_applicant?.assets?.asset_3_type || "",
        asset_3_desc: data?.deals_applicant?.assets?.asset_3_desc || "",
        asset_3_value: data?.deals_applicant?.assets?.asset_3_value ? formatPriceWithDollar(data?.deals_applicant?.assets?.asset_3_value) : "",
      },
      debts: {
        debt_1_creditor: data?.deals_applicant?.debts?.debt_1_creditor || "",
        debt_1_type: data?.deals_applicant?.debts?.debt_1_type || "",
        debt_1_payment: data?.deals_applicant?.debts?.debt_1_payment ? formatPriceWithDollar(data?.deals_applicant?.debts?.debt_1_payment) : "",
        debt_1_balance: data?.deals_applicant?.debts?.debt_1_balance ? formatPriceWithDollar(data?.deals_applicant?.debts?.debt_1_balance) : "",
        debt_2_creditor: data?.deals_applicant?.debts?.debt_2_creditor || "",
        debt_2_type: data?.deals_applicant?.debts?.debt_2_type || "",
        debt_2_payment: data?.deals_applicant?.debts?.debt_2_payment ? formatPriceWithDollar(data?.deals_applicant?.debts?.debt_2_payment) : "",
        debt_2_balance: data?.deals_applicant?.debts?.debt_2_balance ? formatPriceWithDollar(data?.deals_applicant?.debts?.debt_2_balance) : "",
        debt_3_creditor: data?.deals_applicant?.debts?.debt_3_creditor || "",
        debt_3_type: data?.deals_applicant?.debts?.debt_3_type || "",
        debt_3_payment: data?.deals_applicant?.debts?.debt_3_payment ? formatPriceWithDollar(data?.deals_applicant?.debts?.debt_3_payment) : "",
        debt_3_balance: data?.deals_applicant?.debts?.debt_3_balance ? formatPriceWithDollar(data?.deals_applicant?.debts?.debt_3_balance) : "",
      },
      dependents: {
        dependents_count: data?.deals_applicant?.dependents?.dependents_count ? String(data?.deals_applicant?.dependents?.dependents_count) : "",
        dependents_1_age: data?.deals_applicant?.dependents?.dependents_1_age ? String(data?.deals_applicant?.dependents?.dependents_1_age) : "",
        dependents_2_age: data?.deals_applicant?.dependents?.dependents_2_age ? String(data?.deals_applicant?.dependents?.dependents_2_age) : "",
        dependents_3_age: data?.deals_applicant?.dependents?.dependents_3_age ? String(data?.deals_applicant?.dependents?.dependents_3_age) : "",
        dependents_4_age: data?.deals_applicant?.dependents?.dependents_4_age ? String(data?.deals_applicant?.dependents?.dependents_4_age) : "",
        dependents_5_age: data?.deals_applicant?.dependents?.dependents_5_age ? String(data?.deals_applicant?.dependents?.dependents_5_age) : "",
        dependents_6_age: data?.deals_applicant?.dependents?.dependents_6_age ? String(data?.deals_applicant?.dependents?.dependents_6_age) : "",
        dependents_7_age: data?.deals_applicant?.dependents?.dependents_7_age ? String(data?.deals_applicant?.dependents?.dependents_7_age) : "",
      },
      prior_repossession: Boolean(data?.deals_applicant?.prior_repossession) || false,
      repossession_year: data?.deals_applicant?.repossession_year ? String(data?.deals_applicant?.repossession_year) : "",
      repossession_reason: data?.deals_applicant?.repossession_reason || "",
      prior_bankruptcy: Boolean(data?.deals_applicant?.prior_bankruptcy) || false,
      bankruptcy_type: data?.deals_applicant?.bankruptcy_type || "",
      bankruptcy_discharge: data?.deals_applicant?.bankruptcy_discharge || "",
      bankruptcy_reason: data?.deals_applicant?.bankruptcy_reason || "",
      currently_in_bankruptcy: Boolean(data?.deals_applicant?.currently_in_bankruptcy) || false,
      future_bankruptcy_anticipated: Boolean(data?.deals_applicant?.future_bankruptcy_anticipated) || false,
      prior_judgment: Boolean(data?.deals_applicant?.prior_judgment) || false,
      judgment_amount: data?.deals_applicant?.judgment_amount || "",
      judgment_to_whom: data?.deals_applicant?.judgment_to_whom || "",
      currently_guarantor: Boolean(data?.deals_applicant?.currently_guarantor) || false,
      guarantor_for_whom: data?.deals_applicant?.guarantor_for_whom || "",
      guarantor_to: data?.deals_applicant?.guarantor_to || "",
    }));

  }, [data]);


  const handleChange = (name, value) => {
    setFormData(prev => {
      // Handle nested object updates (e.g., "current_address.address_1")
      if (name.includes('.')) {
        const keys = name.split('.');
        const newState = { ...prev };
        let current = newState;

        // Navigate to the nested object
        for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) {
            current[keys[i]] = {};
          }
          current = current[keys[i]];
        }

        // Set the final value
        current[keys[keys.length - 1]] = value;

        return newState;
      }

      // Handle regular property updates
      const result = { ...prev, [name]: value };
      return result;
    });
  };

  // Special handler for phone number fields with formatting
  const handlePhoneChange = (name, value) => {
    const formattedValue = formatPhoneNumber(value);
    handleChange(name, formattedValue);
  };

  const handleIdentityInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setIdentityData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };



  const handleSave = async () => {
    const payload = {
      customer: {
        alt_id_type: formData.alt_id_type,
        alt_id_number: formData.alt_id_number,
        alt_id_country: formData.alt_id_country,
        alt_id_state: formData.alt_id_state,
        full_name: formData.full_name,
        ssn: formData.ssn,
        date_of_birth: formatDateForAPI(formData.date_of_birth),
        gender: formData.gender,
        active_service_member: Boolean(formData.active_service_member),
        dl: formData.dl,
        dl_date: formatDateForAPI(formData.dl_date),
        dl_state: formData.dl_state,
        dl_exp: formatDateForAPI(formData.dl_exp),
        marital_status: formData.marital_status,
        is_business: Boolean(formData.is_business),
        doing_business: formData.doing_business,
        fed_id: formData.fed_id,
        email: formData.email,
        cell_phone: formData.cell_phone,
        home_phone: formData.home_phone,
        home_phone_2: formData.home_phone_2,
        work_phone: formData.work_phone,
        work_extension: formData.work_extension,
        work_phone_2: formData.work_phone_2,
        work_extension_2: formData.work_extension_2,
        // Current Address
        current_address: {
          country: formData.current_address.country,
          address_1: formData.current_address.address_1,
          address_2: formData.current_address.address_2,
          zip_code: formData.current_address.zip_code,
          city: formData.current_address.city,
          county: formData.current_address.county,
          state: formData.current_address.state,
          state_name: formData.current_address.state_name,
          years_at: Number(formData.current_address.years_at),
          months_at: Number(formData.current_address.months_at),
          lien_holder: formData.current_address.lien_holder,
          lien_holder_phone: formData.current_address.lien_holder_phone,
          residence_payment: Number(String(formData.current_address.residence_payment).replace(/[^\d.\-]/g, "")),
          residence_type: formData.current_address.residence_type,
          miles_from_lot: Number(formData.current_address.miles_from_lot),
        },
        // Previous Address
        previous_address: {
          prev_country: formData.previous_address.prev_country,
          prev_address_1: formData.previous_address.prev_address_1,
          prev_address_2: formData.previous_address.prev_address_2,
          prev_zip_code: formData.previous_address.prev_zip_code,
          prev_city: formData.previous_address.prev_city,
          prev_county: formData.previous_address.prev_county,
          prev_state: formData.previous_address.prev_state,
          prev_state_name: formData.previous_address.prev_state_name,
          prev_years_at: Number(formData.previous_address.prev_years_at),
          prev_months_at: Number(formData.previous_address.prev_months_at),
          prev_lien_holder: formData.previous_address.prev_lien_holder,
          prev_lien_holder_phone: formData.previous_address.prev_lien_holder_phone,
          prev_residence_payment: Number(String(formData.previous_address.prev_residence_payment).replace(/[^\d.\-]/g, "")),
          prev_residence_type: formData.previous_address.prev_residence_type,
          prev_miles_from_lot: Number(formData.previous_address.prev_miles_from_lot),
        },
        // Current Employment
        current_employment: {
          employer: formData.current_employment.employer,
          supervisor_phone: formData.current_employment.supervisor_phone,
          position: formData.current_employment.position,
          type: formData.current_employment.type,
          income: Number(String(formData.current_employment.income).replace(/[^\d.\-]/g, "")),
          pay_frequency: formData.current_employment.pay_frequency,
          years_at: Number(formData.current_employment.years_at),
          months_at: Number(formData.current_employment.months_at),
          address_1: formData.current_employment.address_1,
          address_2: formData.current_employment.address_2,
          zip_code: formData.current_employment.zip_code,
          city: formData.current_employment.city,
          county: formData.current_employment.county,
          state: formData.current_employment.state,
          country: formData.current_employment.country,
          email: formData.current_employment.email,
        },
        // Previous Employment
        previous_employment: {
          employer: formData.previous_employment.employer,
          supervisor_phone: formData.previous_employment.supervisor_phone,
          position: formData.previous_employment.position,
          type: formData.previous_employment.type,
          income: Number(String(formData.previous_employment.income).replace(/[^\d.\-]/g, "")),
          pay_frequency: formData.previous_employment.pay_frequency,
          years_at: Number(formData.previous_employment.years_at),
          months_at: Number(formData.previous_employment.months_at),
          address_1: formData.previous_employment.address_1,
          address_2: formData.previous_employment.address_2,
          zip_code: formData.previous_employment.zip_code,
          city: formData.previous_employment.city,
          county: formData.previous_employment.county,
          state: formData.previous_employment.state,
          country: formData.previous_employment.country,
          email: formData.previous_employment.email,
        },
      },
      applicant: {
        other_monthly_income: {
          other_income_1_type: formData.other_monthly_income.other_income_1_type,
          other_income_1_desc: formData.other_monthly_income.other_income_1_desc,
          other_income_1_amt: Number(String(formData.other_monthly_income.other_income_1_amt).replace(/[^\d.\-]/g, "")),
          other_income_2_type: formData.other_monthly_income.other_income_2_type,
          other_income_2_desc: formData.other_monthly_income.other_income_2_desc,
          other_income_2_amt: Number(String(formData.other_monthly_income.other_income_2_amt).replace(/[^\d.\-]/g, "")),
          other_income_3_type: formData.other_monthly_income.other_income_3_type,
          other_income_3_desc: formData.other_monthly_income.other_income_3_desc,
          other_income_3_amt: Number(String(formData.other_monthly_income.other_income_3_amt).replace(/[^\d.\-]/g, "")),
        },
        assets: {
          asset_1_type: formData.assets.asset_1_type,
          asset_1_desc: formData.assets.asset_1_desc,
          asset_1_value: Number(String(formData.assets.asset_1_value).replace(/[^\d.\-]/g, "")),
          asset_2_type: formData.assets.asset_2_type,
          asset_2_desc: formData.assets.asset_2_desc,
          asset_2_value: Number(String(formData.assets.asset_2_value).replace(/[^\d.\-]/g, "")),
          asset_3_type: formData.assets.asset_3_type,
          asset_3_desc: formData.assets.asset_3_desc,
          asset_3_value: Number(String(formData.assets.asset_3_value).replace(/[^\d.\-]/g, "")),
        },
        // Debts
        debts: {
          debt_1_creditor: formData.debts.debt_1_creditor,
          debt_1_type: formData.debts.debt_1_type,
          debt_1_payment: Number(String(formData.debts.debt_1_payment).replace(/[^\d.\-]/g, "")),
          debt_1_balance: Number(String(formData.debts.debt_1_balance).replace(/[^\d.\-]/g, "")),
          debt_2_creditor: formData.debts.debt_2_creditor,
          debt_2_type: formData.debts.debt_2_type,
          debt_2_payment: Number(String(formData.debts.debt_2_payment).replace(/[^\d.\-]/g, "")),
          debt_2_balance: Number(String(formData.debts.debt_2_balance).replace(/[^\d.\-]/g, "")),
          debt_3_creditor: formData.debts.debt_3_creditor,
          debt_3_type: formData.debts.debt_3_type,
          debt_3_payment: Number(String(formData.debts.debt_3_payment).replace(/[^\d.\-]/g, "")),
          debt_3_balance: Number(String(formData.debts.debt_3_balance).replace(/[^\d.\-]/g, "")),
        },
        dependents: {
          dependents_count: Number(formData.dependents.dependents_count),
          dependents_1_age: Number(formData.dependents.dependents_1_age),
          dependents_2_age: Number(formData.dependents.dependents_2_age),
          dependents_3_age: Number(formData.dependents.dependents_3_age),
          dependents_4_age: Number(formData.dependents.dependents_4_age),
          dependents_5_age: Number(formData.dependents.dependents_5_age),
          dependents_6_age: Number(formData.dependents.dependents_6_age),
          dependents_7_age: Number(formData.dependents.dependents_7_age),
        },
        prior_repossession: Boolean(formData.prior_repossession),
        repossession_year: formData.repossession_year ? Number(formData.repossession_year) : null,
        repossession_reason: formData.repossession_reason || "",
        prior_bankruptcy: Boolean(formData.prior_bankruptcy),
        bankruptcy_type: formData.bankruptcy_type || "",
        bankruptcy_discharge: formatDateForAPI(formData.bankruptcy_discharge),
        bankruptcy_reason: formData.bankruptcy_reason || "",
        currently_in_bankruptcy: Boolean(formData.currently_in_bankruptcy),
        future_bankruptcy_anticipated: Boolean(formData.future_bankruptcy_anticipated),
        prior_judgment: Boolean(formData.prior_judgment),
        judgment_amount: formData.judgment_amount || "",
        judgment_to_whom: formData.judgment_to_whom || "",
        currently_guarantor: Boolean(formData.currently_guarantor),
        guarantor_for_whom: formData.guarantor_for_whom || "",
        guarantor_to: formData.guarantor_to || "",
      },
    };

    try {
      await updateDealMutation.mutateAsync({ id, data: payload });
      return { success: true };
    } catch (e) {
      return { success: false };
    }
  };

  useImperativeHandle(ref, () => ({ handleSave }));

  const subTabs = useMemo(() => [t('dealApplicantTab.info'), t('dealApplicantTab.credit'), t('dealApplicantTab.references')], [t]);

  const handleSubmitApplicantReference = () => {
    const payload = {
      full_name: identityData.full_name,
      ssn: identityData.ssn,
      relationship_type: identityData.relationship_type || "Unknown",
      relationship_customer_id: data?.customer?.id,
      deal_id: data?.documentId,
      tab: tab
    };

    createCustomerMutation.mutate(payload);
  };


  return (
    <div
      className={classNames("w-100 deal-applicant-tab", {
        "deal-applicant-tab--theme2": isTheme2,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <div className="d-flex align-items-center justify-content-between">
        <Nav tabs className="border-0" style={{ borderBottom: "none", paddingTop: 0 }}>
          {subTabs.map((tab) => (
            <NavItem key={tab}>
              <NavLink
                href="#"
                className={activeSubTab === tab ? "active font-weight-bold" : "text-muted"}
                style={{
                  background: "none",
                  border: "none",
                  // fontSize: 16,
                  // marginRight: 8,
                  position: "relative",
                  // fontWeight: 500,
                  cursor: 'pointer',
                  color: activeSubTab === tab ? activeColorStyle.borderColor : undefined,
                  fontWeight: activeSubTab === tab ? 700 : 500
                }}
                onClick={(e) => {
                  e.preventDefault();
                  handleSubTabChange(tab);
                }}
              >
                {tab}
                {activeSubTab === tab && (
                  <div style={{ height: 3, background: activeColorStyle.borderColor, borderRadius: 2, position: "absolute", left: 0, right: 0, bottom: -8 }} />
                )}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      <TabContent activeTab={activeSubTab}>
        <TabPane tabId={t('dealApplicantTab.info')}>
          <div className={classNames({ "deal-applicant-identity-fields": isTheme2 })}>
          <Row>
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.identityInformation')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="full_name">{t('dealApplicantTab.fullName')}</Label>
                        <Input
                          id="full_name"
                          type="text"
                          value={formData.full_name}
                          onChange={(e) => handleChange("full_name", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterFullName')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="ssn">{t('dealApplicantTab.ssn')}</Label>
                        <Input
                          id="ssn"
                          type="text"
                          value={formData.ssn}
                          onChange={(e) => handleChange("ssn", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterSSN')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>


                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="date_of_birth">{t('dealApplicantTab.dateOfBirth')}</Label>
                        <ReactDatetime
                          inputProps={{
                            className: "form-control",
                            placeholder: t('dealApplicantTab.selectDate'),
                            style: dateInputStyle
                          }}
                          value={formData.date_of_birth ? formatDateForPicker(formData.date_of_birth) : formData.date_of_birth}
                          onChange={(date) => handleChange("date_of_birth", date)}
                          dateFormat="MM-DD-YYYY"
                          timeFormat={false}
                          closeOnSelect={true}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="gender">{t('dealApplicantTab.gender')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectGender')}
                          name="gender"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.gender ? {
                            value: formData.gender,
                            label: formData.gender === "Male" ? t('dealApplicantTab.male') : t('dealApplicantTab.female')
                          } : null}
                          onChange={(option) => handleChange("gender", option?.value || '')}
                          options={[
                            { value: "Male", label: t('dealApplicantTab.male') },
                            { value: "Female", label: t('dealApplicantTab.female') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup check className="mt-2">
                        <Label check>
                          <Input
                            style={inputStyle14}
                            type="checkbox"
                            className="form-check-input"
                            checked={!!formData.is_business}
                            onChange={(e) => handleChange("is_business", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          Is a Business
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="doing_business">{t('dealApplicantTab.doingBusinessAs')}</Label>
                        <Input
                          id="doing_business"
                          type="text"
                          disabled={!formData.is_business}
                          value={formData.doing_business}
                          onChange={(e) => handleChange("doing_business", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterDBA')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="fed_id">{t('dealApplicantTab.fedID')}</Label>
                        <Input
                          id="fed_id"
                          type="text"
                          disabled={!formData.is_business}
                          value={formData.fed_id}
                          onChange={(e) => handleChange("fed_id", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterFederalID')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>

            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.driversLicenseInformation')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="dl_number">{t('dealApplicantTab.dl')}</Label>
                        <Input
                          id="dl"
                          type="text"
                          value={formData.dl}
                          onChange={(e) => handleChange("dl", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterDLNumber')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="dl_date">{t('dealApplicantTab.dlIssueDate')}</Label>
                        <ReactDatetime
                          inputProps={{
                            className: "form-control",
                            placeholder: t('dealApplicantTab.selectDate'),
                            style: dateInputStyle
                          }}
                          value={formData.dl_date ? formatDateForPicker(formData.dl_date) : formData.dl_date}
                          onChange={(date) => handleChange("dl_date", date)}
                          dateFormat="MM-DD-YYYY"
                          timeFormat={false}
                          closeOnSelect={true}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="dl_state">{t('dealApplicantTab.dlState')}</Label>
                        <StatesInput
                          name="dl_state"
                          value={formData.dl_state ? { id: Number(formData.dl_state.id) } : null}
                          onChange={(stateObject) => {
                            handleChange("dl_state", stateObject ? { id: Number(stateObject.id) } : null);
                          }}
                          activeColor={activeColor}
                          styles={selectStyles}
                          placeholder={t('dealApplicantTab.selectState')}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="dl_exp">{t('dealApplicantTab.dlExpiration')}</Label>
                        <ReactDatetime
                          inputProps={{
                            className: "form-control",
                            placeholder: t('dealApplicantTab.selectDate'),
                            style: dateInputStyle
                          }}
                          value={formData.dl_exp ? formatDateForPicker(formData.dl_exp) : formData.dl_exp}
                          onChange={(date) => handleChange("dl_exp", date)}
                          dateFormat="MM-DD-YYYY"
                          timeFormat={false}
                          closeOnSelect={true}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label for="marital_status">{t('dealApplicantTab.maritalStatus')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectStatus')}
                          name="marital_status"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.marital_status ? {
                            value: formData.marital_status,
                            label: formData.marital_status === "Single" ? t('dealApplicantTab.single') :
                              formData.marital_status === "Married" ? t('dealApplicantTab.married') :
                                formData.marital_status === "Divorced" ? t('dealApplicantTab.divorced') :
                                  formData.marital_status === "Widowed" ? t('dealApplicantTab.widowed') : formData.marital_status
                          } : null}
                          onChange={(option) => handleChange("marital_status", option?.value || '')}
                          options={[
                            { value: "Single", label: t('dealApplicantTab.single') },
                            { value: "Married", label: t('dealApplicantTab.married') },
                            { value: "Divorced", label: t('dealApplicantTab.divorced') },
                            { value: "Widowed", label: t('dealApplicantTab.widowed') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.contactInformation')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="email">{t('dealApplicantTab.email')}</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => handleChange("email", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterEmail')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="cell_phone">{t('dealApplicantTab.cellPhone')}</Label>
                        <Input
                          id="cell_phone"
                          type="tel"
                          value={formData.cell_phone}
                          onChange={(e) => handlePhoneChange("cell_phone", e.target.value)}
                          className="form-control"
                          placeholder="Cell Phone (e.g., +1-555-123-4567)"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="home_phone">{t('dealApplicantTab.homePhone')}</Label>
                        <Input
                          id="home_phone"
                          type="tel"
                          value={formData.home_phone}
                          onChange={(e) => handlePhoneChange("home_phone", e.target.value)}
                          className="form-control"
                          placeholder="Home Phone (e.g., +1-555-123-4567)"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="home_phone_2">{t('dealApplicantTab.homePhone2')}</Label>
                        <Input
                          id="home_phone_2"
                          type="tel"
                          value={formData.home_phone_2}
                          onChange={(e) => handlePhoneChange("home_phone_2", e.target.value)}
                          className="form-control"
                          placeholder="Home Phone 2 (e.g., +1-555-123-4567)"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="work_phone">{t('dealApplicantTab.workPhone')}</Label>
                        <Input
                          id="work_phone"
                          type="text"
                          value={formData.work_phone}
                          onChange={(e) => handlePhoneChange("work_phone", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterWorkPhone')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="work_extension">{t('dealApplicantTab.workExtension')}</Label>
                        <Input
                          id="work_extension"
                          type="text"
                          value={formData.work_extension}
                          onChange={(e) => handleChange("work_extension", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterWorkExtension')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="work_phone_2">{t('dealApplicantTab.workPhone2')}</Label>
                        <Input
                          id="work_phone_2"
                          type="text"
                          value={formData.work_phone_2}
                          onChange={(e) => handlePhoneChange("work_phone_2", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterWorkPhone2')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="work_extension_2">{t('dealApplicantTab.workExtension2')}</Label>
                        <Input
                          id="work_extension_2"
                          type="text"
                          value={formData.work_extension_2}
                          onChange={(e) => handleChange("work_extension_2", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterWorkExtension2')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.alternateIdentityInfo')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="alt_id_type">{t('dealApplicantTab.alternateGovtIDType')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder="Select ID Type"
                          name="alt_id_type"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.alt_id_type ? {
                            value: formData.alt_id_type,
                            label: formData.alt_id_type === "1" ? "U.S. Passport" :
                              formData.alt_id_type === "2" ? "U.S. State Identification" :
                                formData.alt_id_type === "3" ? "U.S. Military ID" :
                                  formData.alt_id_type === "4" ? "U.S. Department of Homeland Security ID" :
                                    formData.alt_id_type === "5" ? "U.S. Citizens & Immigration Services ID" :
                                      formData.alt_id_type === "6" ? "U.S. Department of State ID" :
                                        formData.alt_id_type === "7" ? "Photo ID" :
                                          formData.alt_id_type === "8" ? "Matricula Consular" :
                                            formData.alt_id_type === "9" ? "Foreign Passport" :
                                              formData.alt_id_type === "10" ? "NATO ID" :
                                                formData.alt_id_type === "11" ? "Other Military Status of Forces Photo ID" : formData.alt_id_type
                          } : null}
                          onChange={(option) => handleChange("alt_id_type", option?.value || '')}
                          options={[
                            { value: "1", label: "U.S. Passport" },
                            { value: "2", label: "U.S. State Identification" },
                            { value: "3", label: "U.S. Military ID" },
                            { value: "4", label: "U.S. Department of Homeland Security ID" },
                            { value: "5", label: "U.S. Citizens & Immigration Services ID" },
                            { value: "6", label: "U.S. Department of State ID" },
                            { value: "7", label: "Photo ID" },
                            { value: "8", label: "Matricula Consular" },
                            { value: "9", label: "Foreign Passport" },
                            { value: "10", label: "NATO ID" },
                            { value: "11", label: "Other Military Status of Forces Photo ID" }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="alt_id_number">{t('dealApplicantTab.alternateGovtIDNumber')}</Label>
                        <Input
                          id="alt_id_number"
                          type="text"
                          value={formData.alt_id_number}
                          onChange={(e) => handleChange("alt_id_number", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterIDNumber')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="alt_id_country">{t('dealApplicantTab.alternateGovtIDCountry')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('selectCountry')}
                          name="alt_id_country"
                          isSearchable
                          closeMenuOnSelect={true}
                          value={formData.alt_id_country ? {
                            value: formData.alt_id_country,
                            label: countriesData?.data?.find(country => String(country.id) === String(formData.alt_id_country))?.name || formData.alt_id_country
                          } : null}
                          onChange={(option) => {
                            handleChange("alt_id_country", option?.value || "");
                            handleChange("alt_id_state", "");
                          }}
                          options={[
                            { value: '', label: t('selectCountry') },
                            ...(countriesData?.data?.map(country => ({
                              value: String(country.id),
                              label: country.name
                            })) || [])
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="alt_id_state">{t('dealApplicantTab.alternateGovtIDState')}</Label>
                        <StatesInput
                          name="alt_id_state"
                          value={formData.alt_id_state ? { id: formData.alt_id_state?.id } : null}
                          onChange={(stateObject) => {
                            handleChange("alt_id_state", stateObject ? { id: Number(stateObject.id) } : null);
                          }}
                          activeColor={activeColor}
                          styles={selectStyles}
                          placeholder={t('selectState')}
                          countrySelected={!!formData.alt_id_country}
                          isDisabled={!altIdHasStates}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.currentAddress')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="country">{t('dealApplicantTab.country')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('selectCountry')}
                          name="country"
                          isSearchable
                          closeMenuOnSelect={true}
                          value={formData.current_address?.country ? {
                            value: formData.current_address.country,
                            label: countriesData?.data?.find(country => String(country.id) === String(formData.current_address.country))?.name || formData.current_address.country
                          } : null}
                          onChange={(option) => {
                            handleChange("current_address.country", option?.value || "");
                            handleChange("current_address.state", "");
                          }}
                          options={[
                            { value: '', label: t('selectCountry') },
                            ...(countriesData?.data?.map(country => ({
                              value: String(country.id),
                              label: country.name
                            })) || [])
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="address_1">{t('dealApplicantTab.address1')}</Label>
                        <Input
                          id="address_1"
                          type="text"
                          value={formData.current_address?.address_1}
                          onChange={(e) => handleChange("current_address.address_1", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress1')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="address_2">{t('dealApplicantTab.address2')}</Label>
                        <Input
                          id="address_2"
                          type="text"
                          value={formData.current_address?.address_2}
                          onChange={(e) => handleChange("current_address.address_2", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress2')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label for="zip_code">{t('dealApplicantTab.zipCode')}</Label>
                        <Input
                          id="zip_code"
                          type="text"
                          value={formData.current_address?.zip_code}
                          onChange={(e) => handleChange("current_address.zip_code", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterZIP')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="city">{t('dealApplicantTab.city')}</Label>
                        <Input
                          id="city"
                          type="text"
                          value={formData.current_address?.city}
                          onChange={(e) => handleChange("current_address.city", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCity')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="county">{t('dealApplicantTab.county')}</Label>
                        <Input
                          id="county"
                          type="text"
                          value={formData.current_address?.county}
                          onChange={(e) => handleChange("current_address.county", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCounty')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="state">{t('dealApplicantTab.state')}</Label>
                        <StatesInput
                          name="current_address.state"
                          value={formData.current_address?.state ? { id: Number(formData.current_address.state) } : null}
                          onChange={(stateObject) => {
                            handleChange("current_address.state", stateObject ? String(stateObject.id) : "");
                            handleChange("current_address.state_name", stateObject ? stateObject.abbreviation : "");
                          }}
                          activeColor={activeColor}
                          styles={selectStyles}
                          placeholder={t('selectState')}
                          countrySelected={!!formData.current_address?.country}
                          isDisabled={!currAddrHasStates}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="years_at">{t('dealApplicantTab.yearsAt')}</Label>
                        <Input
                          id="years_at"
                          type="number"
                          min="0"
                          value={formData.current_address?.years_at}
                          onChange={(e) => handleChange("current_address.years_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="months_at">{t('dealApplicantTab.monthsAt')}</Label>
                        <Input
                          id="months_at"
                          type="number"
                          min="0"
                          max="11"
                          value={formData.current_address?.months_at}
                          onChange={(e) => handleChange("current_address.months_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="lien_holder">{t('dealApplicantTab.lienHolder')}</Label>
                        <Input
                          id="lien_holder"
                          type="text"
                          value={formData.current_address?.lien_holder}
                          onChange={(e) => handleChange("current_address.lien_holder", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterLienHolder')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="lien_holder_phone">{t('dealApplicantTab.lienHolderPhone')}</Label>
                        <Input
                          id="lien_holder_phone"
                          type="tel"
                          value={formData.current_address?.lien_holder_phone}
                          onChange={(e) => handlePhoneChange("current_address.lien_holder_phone", e.target.value)}
                          className="form-control"
                          placeholder="Lien Holder Phone (e.g., +1-555-123-4567)"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="residence_payment">{t('dealApplicantTab.residencePayment')}</Label>
                        <Input
                          id="residence_payment"
                          type="text"
                          value={formData.current_address?.residence_payment}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^\d.]/g, "");
                            handleChange("current_address.residence_payment", raw);
                          }}
                          onBlur={() =>
                            handleChange(
                              "current_address.residence_payment",
                              formatPriceWithDollar(formData.current_address?.residence_payment)
                            )
                          }
                          onFocus={() => {
                            const raw = String(formData.current_address?.residence_payment || "").replace(/[^\d.]/g, "");
                            handleChange("current_address.residence_payment", raw);
                          }}
                          className="form-control"
                          placeholder="$0.00"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="residence_type">{t('dealApplicantTab.residenceType')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectResidenceType')}
                          name="current_address.residence_type"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.current_address?.residence_type ? {
                            value: formData.current_address.residence_type,
                            label: formData.current_address.residence_type === "rent" ? t('dealApplicantTab.rent') :
                              formData.current_address.residence_type === "own-mortgage" ? t('dealApplicantTab.ownMortgage') :
                                formData.current_address.residence_type === "own-no-mortgage" ? t('dealApplicantTab.ownNoMortgage') :
                                  formData.current_address.residence_type === "living-with-other" ? t('dealApplicantTab.livingWithOther') : formData.current_address.residence_type
                          } : null}
                          onChange={(option) => handleChange("current_address.residence_type", option?.value || '')}
                          options={[
                            { value: "rent", label: t('dealApplicantTab.rent') },
                            { value: "own-mortgage", label: t('dealApplicantTab.ownMortgage') },
                            { value: "own-no-mortgage", label: t('dealApplicantTab.ownNoMortgage') },
                            { value: "living-with-other", label: t('dealApplicantTab.livingWithOther') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="miles_from_lot">{t('dealApplicantTab.milesFromLot')}</Label>
                        <Input
                          id="miles_from_lot"
                          type="number"
                          min="0"
                          value={formData.current_address?.miles_from_lot}
                          onChange={(e) => handleChange("current_address.miles_from_lot", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.previousAddress')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_country">Country</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('selectCountry')}
                          name="prev_country"
                          isSearchable
                          closeMenuOnSelect={true}
                          value={formData.previous_address?.prev_country ? {
                            value: formData.previous_address.prev_country,
                            label: countriesData?.data?.find(country => String(country.id) === String(formData.previous_address.prev_country))?.name || formData.previous_address.prev_country
                          } : null}
                          onChange={(option) => {
                            handleChange("previous_address.prev_country", option?.value || "");
                            handleChange("previous_address.state", "");
                          }}
                          options={[
                            { value: '', label: t('selectCountry') },
                            ...(countriesData?.data?.map(country => ({
                              value: String(country.id),
                              label: country.name
                            })) || [])
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_address_1">Address 1</Label>
                        <Input
                          id="prev_address_1"
                          type="text"
                          value={formData.previous_address?.prev_address_1}
                          onChange={(e) => handleChange("previous_address.prev_address_1", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress1')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_address_2">Address 2</Label>
                        <Input
                          id="prev_address_2"
                          type="text"
                          value={formData.previous_address?.prev_address_2}
                          onChange={(e) => handleChange("previous_address.prev_address_2", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress2')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label for="prev_zip_code">ZIP Code</Label>
                        <Input
                          id="prev_zip_code"
                          type="text"
                          value={formData.previous_address?.prev_zip_code}
                          onChange={(e) => handleChange("previous_address.prev_zip_code", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterZIP')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="prev_city">City</Label>
                        <Input
                          id="prev_city"
                          type="text"
                          value={formData.previous_address?.prev_city}
                          onChange={(e) => handleChange("previous_address.prev_city", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCity')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="prev_county">County</Label>
                        <Input
                          id="prev_county"
                          type="text"
                          value={formData.previous_address?.prev_county}
                          onChange={(e) => handleChange("previous_address.prev_county", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCounty')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_state">State</Label>
                        <StatesInput
                          name="previous_address.prev_state"
                          value={formData.previous_address?.prev_state ? { id: Number(formData.previous_address.prev_state) } : null}
                          onChange={(stateObject) => {
                            handleChange("previous_address.prev_state", stateObject ? String(stateObject.id) : "");
                          }}
                          activeColor={activeColor}
                          styles={selectStyles}
                          placeholder={t('selectState')}
                          countrySelected={!!formData.previous_address?.prev_country}
                          isDisabled={!prevAddrHasStates}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="prev_years_at">Years At</Label>
                        <Input
                          id="prev_years_at"
                          type="number"
                          min="0"
                          value={formData.previous_address?.prev_years_at}
                          onChange={(e) => handleChange("previous_address.prev_years_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="prev_months_at">Months At</Label>
                        <Input
                          id="prev_months_at"
                          type="number"
                          min="0"
                          max="11"
                          value={formData.previous_address?.prev_months_at}
                          onChange={(e) => handleChange("previous_address.prev_months_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_lien_holder">Lien Holder</Label>
                        <Input
                          id="prev_lien_holder"
                          type="text"
                          value={formData.previous_address?.prev_lien_holder}
                          onChange={(e) => handleChange("previous_address.prev_lien_holder", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterLienHolder')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_lien_holder_phone">Lien Holder Phone</Label>
                        <Input
                          id="prev_lien_holder_phone"
                          type="tel"
                          value={formData.previous_address?.prev_lien_holder_phone}
                          onChange={(e) => handlePhoneChange("previous_address.prev_lien_holder_phone", e.target.value)}
                          className="form-control"
                          placeholder="Previous Lien Holder Phone (e.g., +1-555-123-4567)"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_residence_payment">Residence Pmt</Label>
                        <Input
                          id="prev_residence_payment"
                          type="text"
                          value={formData.previous_address?.prev_residence_payment}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^\d.]/g, "");
                            handleChange("previous_address.prev_residence_payment", raw);
                          }}
                          onBlur={() =>
                            handleChange(
                              "previous_address.prev_residence_payment",
                              formatPriceWithDollar(formData.previous_address?.prev_residence_payment)
                            )
                          }
                          onFocus={() => {
                            const raw = String(formData.previous_address?.prev_residence_payment || "").replace(/[^\d.]/g, "");
                            handleChange("previous_address.prev_residence_payment", raw);
                          }}
                          className="form-control"
                          placeholder="$0.00"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_residence_type">Residence Type</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectResidenceType')}
                          name="previous_address.residence_type"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.previous_address?.prev_residence_type ? {
                            value: formData.previous_address.prev_residence_type,
                            label: formData.previous_address.prev_residence_type === "rent" ? t('dealApplicantTab.rent') :
                              formData.previous_address.prev_residence_type === "own-mortgage" ? t('dealApplicantTab.ownMortgage') :
                                formData.previous_address.prev_residence_type === "own-no-mortgage" ? t('dealApplicantTab.ownNoMortgage') :
                                  formData.previous_address.prev_residence_type === "living-with-other" ? t('dealApplicantTab.livingWithOther') : formData.previous_address.residence_type
                          } : null}
                          onChange={(option) => handleChange("previous_address.prev_residence_type", option?.value || '')}
                          options={[
                            { value: "rent", label: t('dealApplicantTab.rent') },
                            { value: "own-mortgage", label: t('dealApplicantTab.ownMortgage') },
                            { value: "own-no-mortgage", label: t('dealApplicantTab.ownNoMortgage') },
                            { value: "living-with-other", label: t('dealApplicantTab.livingWithOther') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.currentEmployment')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_employer">{t('dealApplicantTab.employmentEmployer')}</Label>
                        <Input
                          id="employment_employer"
                          type="text"
                          value={formData.current_employment?.employer}
                          onChange={(e) => handleChange("current_employment.employer", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterEmployer')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_supervisor_phone">{t('dealApplicantTab.supervisorPhone')}</Label>
                        <Input
                          id="employment_supervisor_phone"
                          type="tel"
                          value={formData.current_employment?.supervisor_phone}
                          onChange={(e) => handlePhoneChange("current_employment.supervisor_phone", e.target.value)}
                          className="form-control"
                          placeholder="Employment Supervisor Phone (e.g., +1-555-123-4567)"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_position">{t('dealApplicantTab.position')}</Label>
                        <Input
                          id="employment_position"
                          type="text"
                          value={formData.current_employment?.position}
                          onChange={(e) => handleChange("current_employment.position", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterPosition')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_type">{t('dealApplicantTab.employmentType')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectType')}
                          name="employment_type"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.current_employment?.type ? {
                            value: formData.current_employment.type,
                            label: formData.current_employment.type === "Full-Time" ? t('dealApplicantTab.fullTime') :
                              formData.current_employment.type === "Part-Time" ? t('dealApplicantTab.partTime') :
                                formData.current_employment.type === "Self-Employed" ? t('dealApplicantTab.selfEmployed') :
                                  formData.current_employment.type === "Contract" ? t('dealApplicantTab.contract') :
                                    formData.current_employment.type === "Unemployed" ? t('dealApplicantTab.unemployed') : formData.current_employment.type
                          } : null}
                          onChange={(option) => handleChange("current_employment.type", option?.value || '')}
                          options={[
                            { value: "Full-Time", label: t('dealApplicantTab.fullTime') },
                            { value: "Part-Time", label: t('dealApplicantTab.partTime') },
                            { value: "Self-Employed", label: t('dealApplicantTab.selfEmployed') },
                            { value: "Contract", label: t('dealApplicantTab.contract') },
                            { value: "Unemployed", label: t('dealApplicantTab.unemployed') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_income">{t('dealApplicantTab.income')}</Label>
                        <Input
                          id="employment_income"
                          type="text"
                          value={formData.current_employment?.income}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^\d.]/g, "");
                            handleChange("current_employment.income", raw);
                          }}
                          onBlur={() =>
                            handleChange(
                              "current_employment.income",
                              formatPriceWithDollar(formData.current_employment.income)
                            )
                          }
                          onFocus={() => {
                            const raw = String(formData.current_employment.income || "").replace(/[^\d.]/g, "");
                            handleChange("current_employment.income", raw);
                          }}
                          className="form-control"
                          placeholder="$0.00"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_pay_frequency">{t('dealApplicantTab.payFrequency')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectFrequency')}
                          name="current_employment.pay_frequency"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.current_employment?.pay_frequency ? {
                            value: formData.current_employment.pay_frequency,
                            label: formData.current_employment.pay_frequency === "Weekly" ? t('dealApplicantTab.weekly') :
                              formData.current_employment.pay_frequency === "Bi-Weekly" ? t('dealApplicantTab.biWeekly') :
                                formData.current_employment.pay_frequency === "Semi-Monthly" ? t('dealApplicantTab.semiMonthly') :
                                  formData.current_employment.pay_frequency === "Monthly" ? t('dealApplicantTab.monthly') :
                                    formData.current_employment.pay_frequency === "Annually" ? t('dealApplicantTab.annually') : formData.current_employment.pay_frequency
                          } : null}
                          onChange={(option) => handleChange("current_employment.pay_frequency", option?.value || '')}
                          options={[
                            { value: "Weekly", label: t('dealApplicantTab.weekly') },
                            { value: "Bi-Weekly", label: t('dealApplicantTab.biWeekly') },
                            { value: "Semi-Monthly", label: t('dealApplicantTab.semiMonthly') },
                            { value: "Monthly", label: t('dealApplicantTab.monthly') },
                            { value: "Annually", label: t('dealApplicantTab.annually') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="3">
                      <FormGroup>
                        <Label for="employment_years_at">{t('dealApplicantTab.yearsAt')}</Label>
                        <Input
                          id="employment_years_at"
                          type="number"
                          min="0"
                          value={formData.current_employment?.years_at}
                          onChange={(e) => handleChange("current_employment.years_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="employment_months_at">{t('dealApplicantTab.monthsAt')}</Label>
                        <Input
                          id="employment_months_at"
                          type="number"
                          min="0"
                          max="11"
                          value={formData.current_employment?.months_at}
                          onChange={(e) => handleChange("current_employment.months_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_address_1">{t('dealApplicantTab.employmentAddress1')}</Label>
                        <Input
                          id="employment_address_1"
                          type="text"
                          value={formData.current_employment?.address_1}
                          onChange={(e) => handleChange("current_employment.address_1", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress1')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_address_2">{t('dealApplicantTab.employmentAddress2')}</Label>
                        <Input
                          id="employment_address_2"
                          type="text"
                          value={formData.current_employment?.address_2}
                          onChange={(e) => handleChange("current_employment.address_2", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress2')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label for="employment_zip_code">{t('dealApplicantTab.employmentZipCode')}</Label>
                        <Input
                          id="employment_zip_code"
                          type="text"
                          value={formData.current_employment?.zip_code}
                          onChange={(e) => handleChange("current_employment.zip_code", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterZIP')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="employment_city">{t('dealApplicantTab.employmentCity')}</Label>
                        <Input
                          id="employment_city"
                          type="text"
                          value={formData.current_employment?.city}
                          onChange={(e) => handleChange("current_employment.city", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCity')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="employment_county">{t('dealApplicantTab.employmentCounty')}</Label>
                        <Input
                          id="employment_county"
                          type="text"
                          value={formData.current_employment?.county}
                          onChange={(e) => handleChange("current_employment.county", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCounty')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_state">{t('dealApplicantTab.employmentState')}</Label>
                        <StatesInput
                          name="current_employment.state"
                          value={formData.current_employment?.state ? { id: Number(formData.current_employment.state) } : null}
                          onChange={(stateObject) => {
                            handleChange("current_employment.state", stateObject ? String(stateObject.id) : "");
                          }}
                          activeColor={activeColor}
                          styles={selectStyles}
                          placeholder={t('selectState')}
                          countrySelected={!!formData.current_employment?.country}
                          isDisabled={!employmentHasStates}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="employment_country">{t('dealApplicantTab.employmentCountry')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('selectCountry')}
                          name="current_employment.country"
                          isSearchable
                          closeMenuOnSelect={true}
                          value={formData.current_employment?.country ? {
                            value: formData.current_employment.country,
                            label: countriesData?.data?.find(country => String(country.id) === String(formData.current_employment.country))?.name || formData.current_employment.country
                          } : null}
                          onChange={(option) => {
                            handleChange("current_employment.country", option?.value || "");
                            handleChange("current_employment.state", "");
                          }}
                          options={[
                            { value: '', label: t('selectCountry') },
                            ...(countriesData?.data?.map(country => ({
                              value: String(country.id),
                              label: country.name
                            })) || [])
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label for="employment_email">{t('dealApplicantTab.employmentEmail')}</Label>
                        <Input
                          id="employment_email"
                          type="email"
                          value={formData.current_employment?.email}
                          onChange={(e) => handleChange("current_employment.email", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterEmail')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.previousEmployment')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_employer">{t('dealApplicantTab.employmentEmployer')}</Label>
                        <Input
                          id="prev_employment_employer"
                          type="text"
                          value={formData.previous_employment?.employer}
                          onChange={(e) => handleChange("previous_employment.employer", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterEmployer')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_supervisor_phone">{t('dealApplicantTab.supervisorPhone')}</Label>
                        <Input
                          id="prev_employment_supervisor_phone"
                          type="tel"
                          value={formData.previous_employment?.supervisor_phone}
                          onChange={(e) => handlePhoneChange("previous_employment.supervisor_phone", e.target.value)}
                          className="form-control"
                          placeholder="Previous Employment Supervisor Phone (e.g., +1-555-123-4567)"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_position">{t('dealApplicantTab.position')}</Label>
                        <Input
                          id="prev_employment_position"
                          type="text"
                          value={formData.previous_employment?.position}
                          onChange={(e) => handleChange("previous_employment.position", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterPosition')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_type">{t('dealApplicantTab.employmentType')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectType')}
                          name="previous_employment.type"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.previous_employment?.type ? {
                            value: formData.previous_employment.type,
                            label: formData.previous_employment.type === "Full-Time" ? t('dealApplicantTab.fullTime') :
                              formData.previous_employment.type === "Part-Time" ? t('dealApplicantTab.partTime') :
                                formData.previous_employment.type === "Self-Employed" ? t('dealApplicantTab.selfEmployed') :
                                  formData.previous_employment.type === "Contract" ? t('dealApplicantTab.contract') :
                                    formData.previous_employment.type === "Unemployed" ? t('dealApplicantTab.unemployed') : formData.previous_employment.type
                          } : null}
                          onChange={(option) => handleChange("previous_employment.type", option?.value || '')}
                          options={[
                            { value: "Full-Time", label: t('dealApplicantTab.fullTime') },
                            { value: "Part-Time", label: t('dealApplicantTab.partTime') },
                            { value: "Self-Employed", label: t('dealApplicantTab.selfEmployed') },
                            { value: "Contract", label: t('dealApplicantTab.contract') },
                            { value: "Unemployed", label: t('dealApplicantTab.unemployed') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_income">{t('dealApplicantTab.income')}</Label>
                        <Input
                          id="prev_employment_income"
                          type="text"
                          value={formData.previous_employment?.income}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^\d.]/g, "");
                            handleChange("previous_employment.income", raw);
                          }}
                          onBlur={() =>
                            handleChange(
                              "previous_employment.income",
                              formatPriceWithDollar(formData.previous_employment?.income)
                            )
                          }
                          onFocus={() => {
                            const raw = String(formData.previous_employment?.income || "").replace(/[^\d.]/g, "");
                            handleChange("previous_employment.income", raw);
                          }}
                          className="form-control"
                          placeholder="$0.00"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_pay_frequency">{t('dealApplicantTab.payFrequency')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectFrequency')}
                          name="previous_employment.pay_frequency"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.previous_employment?.pay_frequency ? {
                            value: formData.previous_employment.pay_frequency,
                            label: formData.previous_employment.pay_frequency === "Weekly" ? t('dealApplicantTab.weekly') :
                              formData.previous_employment.pay_frequency === "Bi-Weekly" ? t('dealApplicantTab.biWeekly') :
                                formData.previous_employment.pay_frequency === "Semi-Monthly" ? t('dealApplicantTab.semiMonthly') :
                                  formData.previous_employment.pay_frequency === "Monthly" ? t('dealApplicantTab.monthly') :
                                    formData.previous_employment.pay_frequency === "Annually" ? t('dealApplicantTab.annually') : formData.previous_employment.pay_frequency
                          } : null}
                          onChange={(option) => handleChange("previous_employment.pay_frequency", option?.value || '')}
                          options={[
                            { value: "Weekly", label: t('dealApplicantTab.weekly') },
                            { value: "Bi-Weekly", label: t('dealApplicantTab.biWeekly') },
                            { value: "Semi-Monthly", label: t('dealApplicantTab.semiMonthly') },
                            { value: "Monthly", label: t('dealApplicantTab.monthly') },
                            { value: "Annually", label: t('dealApplicantTab.annually') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="3">
                      <FormGroup>
                        <Label for="prev_employment_years_at">{t('dealApplicantTab.yearsAt')}</Label>
                        <Input
                          id="prev_employment_years_at"
                          type="number"
                          min="0"
                          value={formData.previous_employment?.years_at}
                          onChange={(e) => handleChange("previous_employment.years_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <Label for="prev_employment_months_at">{t('dealApplicantTab.monthsAt')}</Label>
                        <Input
                          id="prev_employment_months_at"
                          type="number"
                          min="0"
                          max="11"
                          value={formData.previous_employment?.months_at}
                          onChange={(e) => handleChange("previous_employment.months_at", e.target.value)}
                          className="form-control"
                          placeholder="0"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_address_1">{t('dealApplicantTab.employmentAddress1')}</Label>
                        <Input
                          id="prev_employment_address_1"
                          type="text"
                          value={formData.previous_employment?.address_1}
                          onChange={(e) => handleChange("previous_employment.address_1", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress1')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_address_2">{t('dealApplicantTab.employmentAddress2')}</Label>
                        <Input
                          id="prev_employment_address_2"
                          type="text"
                          value={formData.previous_employment?.address_2}
                          onChange={(e) => handleChange("previous_employment.address_2", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterAddress2')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label for="prev_employment_zip_code">{t('dealApplicantTab.employmentZipCode')}</Label>
                        <Input
                          id="prev_employment_zip_code"
                          type="text"
                          value={formData.previous_employment?.zip_code}
                          onChange={(e) => handleChange("previous_employment.zip_code", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterZIP')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="prev_employment_city">{t('dealApplicantTab.employmentCity')}</Label>
                        <Input
                          id="prev_employment_city"
                          type="text"
                          value={formData.previous_employment?.city}
                          onChange={(e) => handleChange("previous_employment.city", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCity')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup>
                        <Label for="prev_employment_county">{t('dealApplicantTab.employmentCounty')}</Label>
                        <Input
                          id="prev_employment_county"
                          type="text"
                          value={formData.previous_employment?.county}
                          onChange={(e) => handleChange("previous_employment.county", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterCounty')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_state">{t('dealApplicantTab.employmentState')}</Label>
                        <StatesInput
                          name="previous_employment.state"
                          value={formData.previous_employment?.state ? { id: Number(formData.previous_employment.state) } : null}
                          onChange={(stateObject) => {
                            handleChange("previous_employment.state", stateObject ? String(stateObject.id) : "");
                          }}
                          activeColor={activeColor}
                          styles={selectStyles}
                          placeholder={t('selectState')}
                          countrySelected={!!formData.previous_employment?.country}
                          isDisabled={!prevEmploymentHasStates}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="prev_employment_country">{t('dealApplicantTab.employmentCountry')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('selectCountry')}
                          name="previous_employment.country"
                          isSearchable
                          closeMenuOnSelect={true}
                          value={formData.previous_employment?.country ? {
                            value: formData.previous_employment.country,
                            label: countriesData?.data?.find(country => String(country.id) === String(formData.previous_employment.country))?.name || formData.previous_employment.country
                          } : null}
                          onChange={(option) => {
                            handleChange("previous_employment.country", option?.value || "");
                            handleChange("previous_employment.state", "");
                          }}
                          options={[
                            { value: '', label: t('selectCountry') },
                            ...(countriesData?.data?.map(country => ({
                              value: String(country.id),
                              label: country.name
                            })) || [])
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label for="prev_employment_email">{t('dealApplicantTab.employmentEmail')}</Label>
                        <Input
                          id="prev_employment_email"
                          type="email"
                          value={formData.previous_employment?.email}
                          onChange={(e) => handleChange("previous_employment.email", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterEmail')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.otherMonthlyIncome')}</div>
                <div className="card-body pt-0">
                  {[1, 2, 3].map((idx) => (
                    <Row key={idx} className={idx > 1 ? "mt-2" : ""}>
                      <Col md="4">
                        <FormGroup>
                          <Label for={`other_monthly_income.other_income_${idx}_type`}>{t('dealApplicantTab.incomeType')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            placeholder={t('dealApplicantTab.selectIncomeType')}
                            name={`other_monthly_income.other_income_${idx}_type`}
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={formData.other_monthly_income[`other_income_${idx}_type`] ? {
                              value: formData.other_monthly_income[`other_income_${idx}_type`],
                              label: formData.other_monthly_income[`other_income_${idx}_type`] === "0" ? "Not Listed" :
                                formData.other_monthly_income[`other_income_${idx}_type`] === "1" ? "Alimony" :
                                  formData.other_monthly_income[`other_income_${idx}_type`] === "2" ? "Child Support" :
                                    formData.other_monthly_income[`other_income_${idx}_type`] === "3" ? "Rental Property" :
                                      formData.other_monthly_income[`other_income_${idx}_type`] === "4" ? "Interest Income" :
                                        formData.other_monthly_income[`other_income_${idx}_type`] === "5" ? "Other Income" : formData.other_monthly_income[`other_income_${idx}_type`]
                            } : null}
                            onChange={(option) => handleChange(`other_monthly_income.other_income_${idx}_type`, option?.value || '')}
                            options={[
                              { value: "0", label: "Not Listed" },
                              { value: "1", label: "Alimony" },
                              { value: "2", label: "Child Support" },
                              { value: "3", label: "Rental Property" },
                              { value: "4", label: "Interest Income" },
                              { value: "5", label: "Other Income" }
                            ]}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for={`other_monthly_income.other_income_${idx}_desc`}>{t('dealApplicantTab.incomeDesc')}</Label>
                          <Input
                            id={`other_monthly_income.other_income_${idx}_desc`}
                            type="text"
                            value={formData.other_monthly_income[`other_income_${idx}_desc`]}
                            onChange={(e) => handleChange(`other_monthly_income.other_income_${idx}_desc`, e.target.value)}
                            className="form-control"
                            placeholder={t('dealApplicantTab.enterDescription')}
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for={`other_monthly_income.other_income_${idx}_amt`}>{t('dealApplicantTab.incomeAmt')}</Label>
                          <Input
                            id={`other_monthly_income.other_income_${idx}_amt`}
                            type="text"
                            value={formData.other_monthly_income[`other_income_${idx}_amt`]}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^\d.]/g, "");
                              handleChange(`other_monthly_income.other_income_${idx}_amt`, raw);
                            }}
                            onBlur={() =>
                              handleChange(
                                `other_monthly_income.other_income_${idx}_amt`,
                                formatPriceWithDollar(formData.other_monthly_income[`other_income_${idx}_amt`])
                              )
                            }
                            onFocus={() => {
                              const raw = String(formData.other_monthly_income[`other_income_${idx}_amt`] || "").replace(/[^\d.]/g, "");
                              handleChange(`other_monthly_income.other_income_${idx}_amt`, raw);
                            }}
                            className="form-control"
                            placeholder="$0.00"
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  ))}
                </div>
              </div>
            </Col>
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.assets')}</div>
                <div className="card-body pt-0">
                  {[1, 2, 3].map((idx) => (
                    <Row key={idx} className={idx > 1 ? "mt-2" : ""}>
                      <Col md="4">
                        <FormGroup>
                          <Label for={`assets.asset_${idx}_type`}>{t('dealApplicantTab.assetType')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            placeholder={t('dealApplicantTab.selectAssetType')}
                            name={`assets.asset_${idx}_type`}
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={formData.assets[`asset_${idx}_type`] ? {
                              value: formData.assets[`asset_${idx}_type`],
                              label: formData.assets[`asset_${idx}_type`] === "1" ? "Checking Account" :
                                formData.assets[`asset_${idx}_type`] === "2" ? "Savings Account" :
                                  formData.assets[`asset_${idx}_type`] === "3" ? "Automobile" :
                                    formData.assets[`asset_${idx}_type`] === "4" ? "Real Estate" :
                                      formData.assets[`asset_${idx}_type`] === "5" ? "CD" :
                                        formData.assets[`asset_${idx}_type`] === "6" ? "Marketable Securities" :
                                          formData.assets[`asset_${idx}_type`] === "7" ? "Other" : formData.assets[`asset_${idx}_type`]
                            } : null}
                            onChange={(option) => handleChange(`assets.asset_${idx}_type`, option?.value || '')}
                            options={[
                              { value: "1", label: "Checking Account" },
                              { value: "2", label: "Savings Account" },
                              { value: "3", label: "Automobile" },
                              { value: "4", label: "Real Estate" },
                              { value: "5", label: "CD" },
                              { value: "6", label: "Marketable Securities" },
                              { value: "7", label: "Other" }
                            ]}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for={`assets.asset_${idx}_desc`}>{t('dealApplicantTab.assetDesc')}</Label>
                          <Input
                            id={`assets.asset_${idx}_desc`}
                            type="text"
                            value={formData.assets[`asset_${idx}_desc`]}
                            onChange={(e) => handleChange(`assets.asset_${idx}_desc`, e.target.value)}
                            className="form-control"
                            placeholder={t('dealApplicantTab.enterDescription')}
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for={`assets.asset_${idx}_value`}>{t('dealApplicantTab.assetValue')}</Label>
                          <Input
                            id={`assets.asset_${idx}_value`}
                            type="text"
                            value={formData.assets[`asset_${idx}_value`]}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^\d.]/g, "");
                              handleChange(`assets.asset_${idx}_value`, raw);
                            }}
                            onBlur={() =>
                              handleChange(
                                `assets.asset_${idx}_value`,
                                formatPriceWithDollar(formData.assets[`asset_${idx}_value`])
                              )
                            }
                            onFocus={() => {
                              const raw = String(formData.assets[`asset_${idx}_value`] || "").replace(/[^\d.]/g, "");
                              handleChange(`assets.asset_${idx}_value`, raw);
                            }}
                            className="form-control"
                            placeholder="$0.00"
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="12">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.debts')}</div>
                <div className="card-body pt-0">
                  {[1, 2, 3].map((idx) => (
                    <Row key={idx} className={idx > 1 ? "mt-2" : ""}>
                      <Col md="3">
                        <FormGroup>
                          <Label for={`debts.debt_${idx}_creditor`}>{t('dealApplicantTab.creditor')}</Label>
                          <Input
                            id={`debts.debt_${idx}_creditor`}
                            type="text"
                            value={formData.debts[`debt_${idx}_creditor`]}
                            onChange={(e) => handleChange(`debts.debt_${idx}_creditor`, e.target.value)}
                            className="form-control"
                            placeholder={t('dealApplicantTab.enterCreditor')}
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for={`debts.debt_${idx}_type`}>{t('dealApplicantTab.debtType')}</Label>
                          <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            placeholder={t('dealApplicantTab.selectDebtType')}
                            name={`debts.debt_${idx}_type`}
                            isSearchable={false}
                            closeMenuOnSelect={true}
                            value={formData.debts[`debt_${idx}_type`] ? {
                              value: formData.debts[`debt_${idx}_type`],
                              label: formData.debts[`debt_${idx}_type`] === "0" ? "Other" :
                                formData.debts[`debt_${idx}_type`] === "1" ? "Mortgage" :
                                  formData.debts[`debt_${idx}_type`] === "2" ? "Rent" :
                                    formData.debts[`debt_${idx}_type`] === "3" ? "Credit Card" :
                                      formData.debts[`debt_${idx}_type`] === "4" ? "Child Support" :
                                        formData.debts[`debt_${idx}_type`] === "5" ? "Alimony" :
                                          formData.debts[`debt_${idx}_type`] === "6" ? "Auto Loan" :
                                            formData.debts[`debt_${idx}_type`] === "7" ? "Loan" :
                                              formData.debts[`debt_${idx}_type`] === "8" ? "Tax Debt" : formData.debts[`debt_${idx}_type`]
                            } : null}
                            onChange={(option) => handleChange(`debts.debt_${idx}_type`, option?.value || '')}
                            options={[
                              { value: "0", label: "Other" },
                              { value: "1", label: "Mortgage" },
                              { value: "2", label: "Rent" },
                              { value: "3", label: "Credit Card" },
                              { value: "4", label: "Child Support" },
                              { value: "5", label: "Alimony" },
                              { value: "6", label: "Auto Loan" },
                              { value: "7", label: "Loan" },
                              { value: "8", label: "Tax Debt" }
                            ]}
                            isClearable
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for={`debts.debt_${idx}_payment`}>{t('dealApplicantTab.payment')}</Label>
                          <Input
                            id={`debts.debt_${idx}_payment`}
                            type="text"
                            value={formData.debts[`debt_${idx}_payment`]}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^\d.]/g, "");
                              handleChange(`debts.debt_${idx}_payment`, raw);
                            }}
                            onBlur={() =>
                              handleChange(
                                `debts.debt_${idx}_payment`,
                                formatPriceWithDollar(formData.debts[`debt_${idx}_payment`])
                              )
                            }
                            onFocus={() => {
                              const raw = String(formData.debts[`debt_${idx}_payment`] || "").replace(/[^\d.]/g, "");
                              handleChange(`debts.debt_${idx}_payment`, raw);
                            }}
                            className="form-control"
                            placeholder="$0.00"
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for={`debts.debt_${idx}_balance`}>{t('dealApplicantTab.currentBalance')}</Label>
                          <Input
                            id={`debts.debt_${idx}_balance`}
                            type="text"
                            value={formData.debts[`debt_${idx}_balance`]}
                            onChange={(e) => {
                              const raw = e.target.value.replace(/[^\d.]/g, "");
                              handleChange(`debts.debt_${idx}_balance`, raw);
                            }}
                            onBlur={() =>
                              handleChange(
                                `debts.debt_${idx}_balance`,
                                formatPriceWithDollar(formData.debts[`debt_${idx}_balance`])
                              )
                            }
                            onFocus={() => {
                              const raw = String(formData.debts[`debt_${idx}_balance`] || "").replace(/[^\d.]/g, "");
                              handleChange(`debts.debt_${idx}_balance`, raw);
                            }}
                            className="form-control"
                            placeholder="$0.00"
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                  ))}
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.dependents')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label for="dependents_count">{t('dealApplicantTab.numberOfDependents')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('common.select')}
                          name="dependents.dependents_count"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          value={formData.dependents.dependents_count ? {
                            value: formData.dependents.dependents_count,
                            label: formData.dependents.dependents_count
                          } : null}
                          onChange={(option) => handleChange("dependents.dependents_count", option?.value || '')}
                          options={[
                            { value: "1", label: "1" },
                            { value: "2", label: "2" },
                            { value: "3", label: "3" },
                            { value: "4", label: "4" },
                            { value: "5", label: "5" },
                            { value: "6", label: "6" },
                            { value: "7", label: "7" }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    {Array.from({ length: Number(formData.dependents.dependents_count || 0) }, (_, i) => i + 1).map((idx) => (
                      <Col key={idx} md="4" >
                        <FormGroup>
                          <Label for={`dependents_${idx}_age`}>{t('dealApplicantTab.dependentAge', { idx })}</Label>
                          <Input
                            id={`dependents.dependents_${idx}_age`}
                            type="number"
                            min="1"
                            max="100"
                            value={formData.dependents[`dependents_${idx}_age`]}
                            onChange={(e) => handleChange(`dependents.dependents_${idx}_age`, e.target.value)}
                            className="form-control"
                            placeholder={t('dealApplicantTab.age')}
                            style={inputStyle14}
                          />
                        </FormGroup>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.priorRepossession')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="12">
                      <FormGroup check className="mt-2">
                        <Label check>
                          <Input
                            style={inputStyle14}
                            type="checkbox"
                            className="form-check-input"
                            checked={!!formData.prior_repossession}
                            onChange={(e) => handleChange("prior_repossession", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          {t('dealApplicantTab.priorRepossession')}
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <Label for="repossession_year">{t('dealApplicantTab.repossessionYear')}</Label>
                        <Input
                          id="repossession_year"
                          type="number"
                          min="1900"
                          max="2100"
                          disabled={!formData.prior_repossession}
                          value={formData.repossession_year}
                          onChange={(e) => handleChange("repossession_year", e.target.value)}
                          className="form-control"
                          placeholder="YYYY"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="8">
                      <FormGroup>
                        <Label for="repossession_reason">{t('dealApplicantTab.repoReason')}</Label>
                        <Input
                          id="repossession_reason"
                          type="text"
                          disabled={!formData.prior_repossession}
                          value={formData.repossession_reason}
                          onChange={(e) => handleChange("repossession_reason", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterReason')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          <Row className="">
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.bankruptcy')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="4">
                      <FormGroup check className="mt-2">
                        <Label check>
                          <Input
                            style={inputStyle14}
                            type="checkbox"
                            className="form-check-input"
                            checked={!!formData.prior_bankruptcy}
                            onChange={(e) => handleChange("prior_bankruptcy", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          {t('dealApplicantTab.priorBankruptcy')}
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup check className="mt-2">
                        <Label check>
                          <Input
                            style={inputStyle14}
                            type="checkbox"
                            className="form-check-input"
                            checked={!!formData.currently_in_bankruptcy}
                            onChange={(e) => handleChange("currently_in_bankruptcy", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          {t('dealApplicantTab.currentlyInBankruptcy')}
                        </Label>
                      </FormGroup>
                    </Col>
                    <Col md="4">
                      <FormGroup check className="mt-2">
                        <Label check>
                          <Input
                            style={inputStyle14}
                            type="checkbox"
                            className="form-check-input"
                            checked={!!formData.future_bankruptcy_anticipated}
                            onChange={(e) => handleChange("future_bankruptcy_anticipated", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          {t('dealApplicantTab.futureBankruptcyAnticipated')}
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="bankruptcy_type">{t('dealApplicantTab.bankruptcyType')}</Label>
                        <Select
                          activeColor={activeColor}
                          styles={selectStyles}
                          className="react-select"
                          placeholder={t('dealApplicantTab.selectType')}
                          name="bankruptcy_type"
                          isSearchable={false}
                          closeMenuOnSelect={true}
                          isDisabled={!formData.prior_bankruptcy}
                          value={formData.bankruptcy_type ? {
                            value: formData.bankruptcy_type,
                            label: formData.bankruptcy_type === "Chapter 7" ? t('dealApplicantTab.chapter7') :
                              formData.bankruptcy_type === "Chapter 11" ? t('dealApplicantTab.chapter11') :
                                formData.bankruptcy_type === "Chapter 13" ? t('dealApplicantTab.chapter13') : formData.bankruptcy_type
                          } : null}
                          onChange={(option) => handleChange("bankruptcy_type", option?.value || '')}
                          options={[
                            { value: "Chapter 7", label: t('dealApplicantTab.chapter7') },
                            { value: "Chapter 11", label: t('dealApplicantTab.chapter11') },
                            { value: "Chapter 13", label: t('dealApplicantTab.chapter13') }
                          ]}
                          isClearable
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="bankruptcy_discharge">{t('dealApplicantTab.bankruptcyDischarge')}</Label>
                        <ReactDatetime
                          inputProps={{
                            className: "form-control",
                            placeholder: t('dealApplicantTab.selectDate'),
                            style: dateInputStyle,
                            disabled: !formData.prior_bankruptcy,
                          }}
                          value={formData.bankruptcy_discharge ? formatDateForPicker(formData.bankruptcy_discharge) : formData.bankruptcy_discharge}
                          onChange={(date) => handleChange("bankruptcy_discharge", date)}
                          dateFormat="MM-DD-YYYY"
                          timeFormat={false}
                          closeOnSelect={true}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label for="bankruptcy_reason">{t('dealApplicantTab.bankruptcyReason')}</Label>
                        <Input
                          id="bankruptcy_reason"
                          type="text"
                          disabled={!formData.prior_bankruptcy}
                          value={formData.bankruptcy_reason}
                          onChange={(e) => handleChange("bankruptcy_reason", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterReason')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
            <Col md="6">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.judgment')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="12">
                      <FormGroup check className="mt-2">
                        <Label check>
                          <Input
                            style={inputStyle14}
                            type="checkbox"
                            className="form-check-input"
                            checked={!!formData.prior_judgment}
                            onChange={(e) => handleChange("prior_judgment", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          {t('dealApplicantTab.priorJudgment')}
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="judgment_amount">{t('dealApplicantTab.judgmentAmount')}</Label>
                        <Input
                          id="judgment_amount"
                          type="text"
                          disabled={!formData.prior_judgment}
                          value={formData.judgment_amount}
                          onChange={(e) => {
                            const raw = e.target.value.replace(/[^\d.]/g, "");
                            handleChange("judgment_amount", raw);
                          }}
                          onBlur={() =>
                            handleChange(
                              "judgment_amount",
                              formatPriceWithDollar(formData.judgment_amount)
                            )
                          }
                          onFocus={() => {
                            const raw = String(formData.judgment_amount || "").replace(/[^\d.]/g, "");
                            handleChange("judgment_amount", raw);
                          }}
                          className="form-control"
                          placeholder="$0.00"
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="judgment_to_whom">{t('dealApplicantTab.judgmentToWhom')}</Label>
                        <Input
                          id="judgment_to_whom"
                          type="text"
                          disabled={!formData.prior_judgment}
                          value={formData.judgment_to_whom}
                          onChange={(e) => handleChange("judgment_to_whom", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterJudgmentToWhom')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>

          <Row className="">
            <Col md="12">
              <div className="card rounded-0 ">
                <div className="card-header font-weight-bold">{t('dealApplicantTab.guarantor')}</div>
                <div className="card-body pt-0">
                  <Row>
                    <Col md="12">
                      <FormGroup check className="mt-2">
                        <Label check>
                          <Input
                            style={inputStyle14}
                            type="checkbox"
                            className="form-check-input"
                            checked={!!formData.currently_guarantor}
                            onChange={(e) => handleChange("currently_guarantor", e.target.checked)}
                          />
                          <span className="form-check-sign" />
                          {t('dealApplicantTab.currentlyGuarantor')}
                        </Label>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="guarantor_for_whom">{t('dealApplicantTab.guarantorForWhom')}</Label>
                        <Input
                          id="guarantor_for_whom"
                          type="text"
                          disabled={!formData.currently_guarantor}
                          value={formData.guarantor_for_whom}
                          onChange={(e) => handleChange("guarantor_for_whom", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterGuarantorForWhom')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label for="guarantor_to">{t('dealApplicantTab.guarantorTo')}</Label>
                        <Input
                          id="guarantor_to"
                          type="text"
                          disabled={!formData.currently_guarantor}
                          value={formData.guarantor_to}
                          onChange={(e) => handleChange("guarantor_to", e.target.value)}
                          className="form-control"
                          placeholder={t('dealApplicantTab.enterGuarantorTo')}
                          style={inputStyle14}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          </div>
        </TabPane>

        <TabPane tabId={t('dealApplicantTab.credit')}>
          <div className="card rounded-0 ">
            <div className="card-header font-weight-bold">{t('dealApplicantTab.applicantCredit')}</div>
            <div className="card-body pt-0">
              <div className="text-muted">{t('dealApplicantTab.creditDetailsWillBeManaged')}</div>
            </div>
          </div>
        </TabPane>

        <TabPane tabId={t('dealApplicantTab.references')}>
          <div className="card rounded-0">
            <div className="d-flex flex-row justify-content-between">
              <div className="card-header font-weight-bold">{t('dealApplicantTab.applicantReferences')}</div>
              <div className="card-header font-weight-bold">
                <div>

                </div>
                <Button
                  size="sm"
                  style={{ ...activeColorStyle, color: 'white', marginRight: 10 }}
                  onClick={() => {
                    setOpenAddArea(!openAddArea);
                  }}
                >
                  {openAddArea ? "Cancel" : "Add Reference"}
                </Button>
              </div>
            </div>
            <div className="card-body pt-0">
              {!openAddArea && (
                <DealApplicantReferences data={data?.customer?.relationships} isLoading={isLoading} />
              )}
              {openAddArea && (
                <div className={classNames({ "deal-applicant-identity-fields": isTheme2 })}>
                  <IdentityInformationForm
                    identityData={identityData}
                    relation={true}
                    onChange={handleIdentityInputChange}
                    showValidation={true}
                    handleSubmit={handleSubmitApplicantReference}
                    title="Identity Information"
                    activeColor={activeColor}
                    isTheme2={isTheme2}
                  />
                </div>
              )}
              {openAddArea && (

                <div className="d-flex justify-content-end mt-3">
                  <Button
                    type="button"
                    onClick={handleSubmitApplicantReference}
                    style={{ ...activeColorStyle, color: 'white' }}
                    disabled={createCustomerMutation.isLoading || (!identityData.full_name && !identityData.ssn)}
                    block
                  >
                    Add Applicant
                  </Button>
                </div>
              )}
            </div>
          </div>
        </TabPane>
      </TabContent>
    </div>
  );
});

export default DealApplicantTab;

